import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {AssetService} from "../../asset/asset.service";
import {AssetRemovalService} from "../../asset/asset-removal.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {DartCorrectionRequestDialogComponent} from "./dart-correction-request";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {WorkflowStep} from "../workflow-step";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {SiteDesignService} from "../../site-design/site-design.service";
import {AssetForSiteDesignModel} from "../../asset/asset-for-site-design.model";
import {AccessoryService} from "../../accessory/accessory.service";
import {FixAccessoryDialogComponent} from "./fix-accessory-dialog.component";
import {RemoveAssetDialogComponent} from "./remove-assets-dialog.component";
import {get} from "lodash";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./add-assets.template.pug",
  providers: [
    AssetService,
    AssetRemovalService,
    AccessoryService,
    DialogService,
    ConfirmDialogService,
    SiteDesignService,
  ],
})
export class AddAssetsComponent extends WorkflowStep {
  public siteId: string;
  public assets: AssetForSiteDesignModel[] = [];
  public currentPage: number = DEFAULT_PAGE;
  public pageSize: number = DEFAULT_PAGE_LIMIT;
  public maxPages: number = 0;
  public totalCount: number = 0;
  public searchText: string = "";
  public error: Error;
  public showImportCDF: boolean = false;
  public showManualDesign: boolean = false;
  public showAddIndividualAssets: boolean = false;
  public stepName: string = "";
  isMigrationActive: boolean = false;

  constructor(
    private siteDesignService: SiteDesignService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public assetService: AssetService,
    public assetRemovalService: AssetRemovalService,
    public accessoryService: AccessoryService,
    private dialogService: DialogService,
    private confirmDialogService: ConfirmDialogService,
  ) {
    super();
  }

  ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.siteId = workflowTargetInfo.targetId;
    this.stepName = get(this.workflowService.currentStep, "stepName");
    this.showImportCDF = this.stepName.startsWith("importCdf");
    this.showManualDesign = this.stepName.startsWith("manualSiteDesign");
    this.showAddIndividualAssets = this.stepName.startsWith("addIndividualAssets");
    this.workflowService.setStepStatus(WorkflowStepStatus.Completed, workflowTargetInfo.stepName);
    this.loadData();
  }

  @handleErrors()
  async loadData() {
    if (!this.siteId) return;
    this.error = undefined;

    const {rows, count, isMigrationActive} = await this.siteDesignService.getCurrentSiteDesign(
      `${+(this.currentPage) - 1}`,
      `${+(this.pageSize)}`,
      this.searchText,
      this.siteId,
    );
    this.isMigrationActive = isMigrationActive;
    this.assets = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.loadData();
  }

  fileUploaded() {
    this.loadData();
  }

  @handleErrors()
  async remove(asset: AssetForSiteDesignModel) {
    const doRemove = await this.confirmDialogService.confirm(
      "Confirm Removal",
      "Are you sure you want to remove this item?",
    );

    if (!doRemove) return;

    switch (asset.type) {
      case "asset":
        await this.assetService.deleteById(asset.id);
        break;
      case "accessory":
        await this.accessoryService.deleteAccessory(asset.id);
        break;
      default:
        throw new Error("Could not delete this item.");
    }

    this.loadData();
  }

  @handleErrors()
  async edit(asset: AssetForSiteDesignModel) {
    switch (asset.type) {
      case "asset":
        if (asset.deviceKey) {
          await this.assetService.editAsset(asset.id);
        } else {
          await this.assetRemovalService.editAsset(asset.id);
        }
        break;
      case "accessory":
        await this.accessoryService.editAccessory(asset.id);
        break;
      default:
        throw new Error("Could not edit this item.");
    }

    this.loadData();
  }

  async autoSave(): Promise<boolean> {
    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  showDartCorrectionDialog() {
    return DartCorrectionRequestDialogComponent.showDialogModal(
      this.dialogService,
      {projectId: this.workflowService.workflowConfig.projectId},
    );
  }

  @handleErrors()
  async addAccessory() {
    await this.accessoryService.createAccessory(this.workflowTargetInfo.targetId);

    await this.loadData();
  }

  @handleErrors()
  async fixAccessory(asset: AssetForSiteDesignModel) {
    const siteId = this.workflowTargetInfo.targetId;

    await FixAccessoryDialogComponent.show(this.dialogService, asset.id, siteId, asset.serialNumber);

    await this.loadData();
  }

  @handleErrors()
  async removeAsset() {
    const siteId = this.workflowTargetInfo.targetId;

    await RemoveAssetDialogComponent.showAssetModal(this.dialogService, siteId);

    await this.loadData();
  }
}
